<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import {
		Button,
		ButtonV2,
		FlexibleCard,
		Icon,
		Loader,
		Modal,
		Stack,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';

	import {
		gatherTransferHomePrecheck,
		type TransferHomePrecheck,
	} from './shared/transfer-home-precheck';

	export let showModal = false;
	export let unitId = '';
	export let homeName = '';
	export let useV2 = false;

	const dispatch = createEventDispatcher<{
		close: void;
		continue: void;
	}>();

	$: ButtonComponent = useV2 ? ButtonV2 : Button;
	$: displayHomeName = homeName || precheck?.homeName || '';

	let precheck: TransferHomePrecheck | null = null;
	let isLoading = false;
	let loadToken = 0;
	let loadedForUnitId = '';

	$: if (showModal && unitId && unitId !== loadedForUnitId) {
		void loadPrecheck(unitId);
	}

	$: if (!showModal) resetPrecheck();

	function resetPrecheck(): void {
		precheck = null;
		isLoading = false;
		loadedForUnitId = '';
	}

	async function loadPrecheck(id: string): Promise<void> {
		loadedForUnitId = id;
		const token = ++loadToken;
		isLoading = true;
		precheck = null;

		try {
			const data = await gatherTransferHomePrecheck(id);
			if (token !== loadToken) return;

			precheck = data;
		} catch {
			if (token !== loadToken) return;
			toast.open({
				type: 'danger',
				title: $format('error_transfer_home_precheck', {
					default: 'Could not check this home before transfer. Please try again.',
				}),
			});
			isLoading = false;
			onClose();
		} finally {
			if (token === loadToken) {
				isLoading = false;
			}
		}
	}

	function onClose(): void {
		if (isLoading) return;
		showModal = false;
		dispatch('close');
	}

	function onContinue(): void {
		if (isLoading || !precheck) return;
		dispatch('continue');
	}
</script>

<Modal
	{showModal}
	on:close={onClose}
	title={$format('transfer_home_info_title', { default: 'Know Before You Transfer' })}
>
	<svelte:fragment slot="content">
		{#if isLoading}
			<div class="d-flex justify-content-center">
				<Loader size="m" />
			</div>
		{:else if precheck}
			<Stack gap="m">
				<FlexibleCard padding="s">
					<Text styleType="body-small">
						{$format('transfer_home_info_intro', {
							default:
								'When the recipient accepts the transfer of {homeName}, important configurations and set-ups will be removed or canceled automatically.',
							values: { homeName: displayHomeName },
						})}
					</Text>
				</FlexibleCard>

				<div>
					{#if precheck.sharedDevicesOut + precheck.sharedDevicesIn > 0}
						<div class="d-flex align-items-center">
							<span class="info-icon mr-m">
								<Icon icon="far fa-share-nodes" size="s" class="text-danger" />
							</span>
							<div>
								<Text class="text-bold text-small">
									{$format('transfer_home_info_shared_devices_title', {
										default: 'Shared devices',
									})}
								</Text>
								<Text class="text-color-muted text-small">
									{$format('transfer_home_info_shared_devices', {
										default:
											'This home currently has {count} shared devices. The sharing access for all of them will be revoked.',
										values: {
											count: precheck.sharedDevicesOut + precheck.sharedDevicesIn,
										},
									})}
								</Text>
							</div>
						</div>
						<hr class="mt-s mb-s hr-muted" />
					{/if}

					{#if precheck.sharedHomesOut > 0}
						<div class="d-flex align-items-center">
							<span class="info-icon mr-m">
								<Icon
									icon="far fa-arrow-right-arrow-left"
									size="s"
									class="text-danger"
								/>
							</span>
							<div>
								<Text class="text-bold text-small">
									{$format('transfer_home_info_shared_home_title', {
										default: 'Shared home access',
									})}
								</Text>
								<Text class="text-color-muted text-small">
									{$format('transfer_home_info_shared_home', {
										default:
											'This home is currently shared with {count} users. The access for all of them will be revoked.',
										values: { count: precheck.sharedHomesOut },
									})}
								</Text>
							</div>
						</div>
						<hr class="mt-s mb-s hr-muted" />
					{/if}

					{#if precheck.showPremium}
						<div class="d-flex align-items-center">
							<span class="info-icon info-icon-badge mr-m">
								<Icon icon="fas fa-gem" size="s" class="text-danger" />
							</span>
							<div>
								<Text class="text-bold text-small">
									{$format('transfer_home_info_premium_title', {
										default: 'Premium subscription',
									})}
								</Text>
								<Text class="text-color-muted text-small">
									{$format('transfer_home_info_premium', {
										default:
											'This home currently has an active Premium subscription. Auto-renewal will be canceled when the recipient accepts the transfer, and you will no longer be charged. The new owner will keep Premium features until the end of the current billing period.',
									})}
								</Text>
							</div>
						</div>
						<hr class="mt-s mb-s hr-muted" />
					{/if}

					{#if precheck.cameraSlotsCount > 0}
						<div class="d-flex align-items-center">
							<span class="info-icon info-icon-badge mr-m">
								<Icon icon="fas fa-camera" size="s" class="text-danger" />
							</span>
							<div>
								<Text class="text-bold text-small">
									{$format('transfer_home_info_camera_title', {
										default: 'Camera Cloud Storage',
									})}
								</Text>
								<Text class="text-color-muted text-small">
									{$format('transfer_home_info_camera_slots', {
										default:
											'This home currently occupies {count} camera slots on your account. Those slots will free up once the transfer completes.',
										values: { count: precheck.cameraSlotsCount },
									})}
								</Text>
							</div>
						</div>
						<hr class="mt-s mb-s hr-muted" />
					{/if}

					{#if precheck.installersCount > 0}
						<div class="d-flex align-items-center">
							<span class="info-icon mr-m">
								<Icon icon="far fa-user-helmet-safety" size="s" class="text-danger" />
							</span>
							<div>
								<Text class="text-bold text-small">
									{$format('transfer_home_info_installer_title', {
										default: 'Installer',
									})}
								</Text>
								<Text class="text-color-muted text-small">
									{$format('transfer_home_info_installer', {
										default:
											'This home currently has {count} installers linked. The installer links will be removed.',
										values: { count: precheck.installersCount },
									})}
								</Text>
							</div>
						</div>
						<hr class="mt-s mb-s hr-muted" />
					{/if}

					<div class="d-flex align-items-center">
						<span class="info-icon mr-m">
							<img src="./images/red_alexa.svg" alt="red-amazon-alexa" width="20" />
						</span>
						<div>
							<Text class="text-bold text-small">
								{$format('transfer_home_info_alexa_title', {
									default: 'Integration',
								})}
							</Text>
							<Text class="text-color-muted text-small">
								{$format('transfer_home_info_alexa', {
									default:
										'Custom voice commands and routines set up with Alexa will no longer be supported.',
								})}
							</Text>
						</div>
					</div>
					<hr class="mt-s mb-s hr-muted" />
				</div>

				<Text styleType="body-small" class="text-center mt-s mb-s">
					{$format('transfer_home_info_outro', {
						default:
							"If you proceed with the {homeName} transfer, you'll lose your access and your configurations.",
						values: { homeName: displayHomeName },
					})}
				</Text>
			</Stack>
		{/if}
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<svelte:component
			this={ButtonComponent}
			width={useV2 ? undefined : 'm'}
			variant="secondary"
			class="mr-s"
			disabled={isLoading}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={onClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</svelte:component>
		<svelte:component
			this={ButtonComponent}
			width={useV2 ? undefined : 'm'}
			variant="primary"
			disabled={isLoading || !precheck}
			aria-label={$format('button_continue', { default: 'Continue' })}
			on:click={onContinue}
		>
			{$format('button_continue', { default: 'Continue' })}
		</svelte:component>
	</div>
</Modal>

<style>
	.info-icon {
		display: inline-flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
	}

	.info-icon-badge {
		background-color: var(--color-danger-10);
		border-radius: 50%;
	}
</style>
